import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { Send, X, Bot, User } from 'lucide-react'
import { api } from '@/lib/api'
import { useAppStore } from '@/store/app'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

export default function ChatAssistant({ onClose }: { onClose: () => void }) {
  const { getToken } = useAuth()
  const { activePlanId } = useAppStore()
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hi! I'm your MealMap AI assistant. Ask me anything about nutrition, recipes, ingredient substitutions, or how to improve your meals! 🍽️",
    },
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function sendMessage() {
    if (!input.trim() || loading) return

    const userMessage: Message = { role: 'user', content: input.trim() }
    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setLoading(true)

    try {
      const token = await getToken()
      const res = await api.post<{ data: { reply: string } }>(
        '/ai/chat',
        { messages: [...messages, userMessage].slice(-10) },  // send last 10 for context
        token!
      )
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: (res as unknown as { data: { reply: string } }).data.reply },
      ])
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "Sorry, I'm having trouble connecting right now. Please try again in a moment." },
      ])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-brand-50">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-brand-600" />
          <div>
            <p className="text-sm font-semibold text-gray-900">AI Assistant</p>
            <p className="text-xs text-gray-500">Nutrition & recipe help</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-brand-100 text-gray-500">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
              msg.role === 'assistant' ? 'bg-brand-100' : 'bg-gray-200'
            }`}>
              {msg.role === 'assistant'
                ? <Bot className="w-4 h-4 text-brand-600" />
                : <User className="w-4 h-4 text-gray-600" />
              }
            </div>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
              msg.role === 'assistant'
                ? 'bg-white ring-1 ring-gray-200 text-gray-800'
                : 'bg-brand-600 text-white'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2">
            <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center">
              <Bot className="w-4 h-4 text-brand-600" />
            </div>
            <div className="bg-white ring-1 ring-gray-200 rounded-2xl px-3 py-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Ask about nutrition, recipes…"
            className="input flex-1 text-sm"
            disabled={loading}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="btn-primary px-3"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5">Powered by Groq · Llama 3</p>
      </div>
    </div>
  )
}
